import { Alert, Box, Button, TextInput } from "@mantine/core";
import type { FormEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import type { ApiError } from "../../api";
import { AlertPresets } from "../../mantine/mantinePresets";
import { ROUTES } from "../../routes";
import { PasswordResetLayout } from "../Layout/PasswordResetLayout";
import { PublicLayout } from "../Layout/PublicLayout";
import { checkAuth } from "../Login/utils/checkAuth";
import "./PasswordReset.scss";
import { resetPassword } from "./utils/resetPassword";

function PasswordReset() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const emailSent = searchParams.get("emailSent");

  const [email, setEmail] = useState<string>("");
  const [emailError, setEmailError] = useState<string | Array<string> | null>(
    null
  );
  const [passResetInProgress, setPassResetInProgress] =
    useState<boolean>(false);
  const [isReady, setIsReady] = useState<boolean>(false);

  const redirectUrl = useMemo(
    () => searchParams.get("next") ?? ROUTES.productSelectPage,
    [searchParams]
  );

  useEffect(() => {
    const prepareRender = async () => {
      const hasAccess = await checkAuth(redirectUrl);

      setIsReady(hasAccess);
    };

    prepareRender();
  }, [redirectUrl]);

  if (!isReady) return null;

  const handleResetPass = async (e: FormEvent) => {
    e.preventDefault();
    setPassResetInProgress(true);

    try {
      await resetPassword(email);
      navigate(location.pathname + "?emailSent=true");
    } catch (e) {
      setEmailError(
        (e as ApiError<Record<string, Array<string>>>).response?.data?.[
          Object.keys(e.response?.data)?.[0]
        ] || ""
      );
    } finally {
      setPassResetInProgress(false);
    }
  };

  return (
    <PublicLayout>
      <PasswordResetLayout
        description={
          emailSent
            ? "Wenn für Ihre E-Mail-Adresse ein Account existiert, haben wir Ihnen eine E-Mail mit Anweisungen zum Zurücksetzen des Passworts gesendet. Falls Sie diese Mail nicht erhalten haben, stellen Sie bitte sicher, dass Sie die Adresse richtig eingeben und prüfen Sie Ihren Spam-Ordner."
            : "Falls Sie Ihr Passwort vergessen haben, geben Sie bitte Ihre E-Mail-Adresse ein. Sie erhalten eine E-Mail mit Anweisungen, um das Passwort zurückzusetzen."
        }
        title={emailSent ? "E-Mail versendet" : "Passwort vergessen"}
      >
        {!emailSent && (
          <form className="PasswordResetForm" onSubmit={handleResetPass}>
            <Box mb="1rem">
              <TextInput
                label="E-Mail-Adresse"
                name="email"
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
              />

              {emailError && (
                <Alert color={AlertPresets.error.color}>
                  {Array.isArray(emailError) ? (
                    emailError.map((err) => (
                      <>
                        <span key={err}>{err}</span>
                        <br />
                      </>
                    ))
                  ) : (
                    <span>{emailError}</span>
                  )}
                </Alert>
              )}

              <div className="SubmitBtnWrapper">
                <Button
                  className="PasswordSubmitBtn"
                  color="primary"
                  disabled={!email}
                  loading={passResetInProgress}
                  type="submit"
                >
                  Passwort zurücksetzen
                </Button>
              </div>
            </Box>
          </form>
        )}
      </PasswordResetLayout>
    </PublicLayout>
  );
}

export { PasswordReset };
